import React, { useEffect, useState, createElement } from 'react';
import { CheckIcon, XIcon, ShieldCheckIcon, ChevronDownIcon, ChevronUpIcon, ChevronLeftIcon, ChevronRightIcon, StarIcon, CheckCircleIcon } from 'lucide-react';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/bZFu6VfyxqrYop6c53LMrq/Mockup_-_Fisico_Visible.png';
const BONUS_1_IMAGE = 'https://cdn.magicpatterns.com/uploads/3P5mnuLcXubnUsrnPWfu6z/Mockup_-_Bono_1.png';
const BONUS_2_IMAGE = 'https://cdn.magicpatterns.com/uploads/eZE6MujScgnNLVvcC5t9df/Mockup_-_Bono_2.png';
const BONUS_3_IMAGE = 'https://cdn.magicpatterns.com/uploads/4LxNm9kAWFhqg813eutdBv/Mockup_-_Bono_3.png';
const CHECKOUT_LINK = 'https://checkout.tumundoglobal.online/cart/45196290228415:1';
const SECTION_LINK = '#compra';
const PAGE_IMAGES = ['https://cdn.magicpatterns.com/uploads/juFq3GHgD4D51pV6whSG5h/Hoja_1.jpg', 'https://cdn.magicpatterns.com/uploads/8Fh2kZuWMoPpgkfP3dbBjW/Hoja_2.jpg', 'https://cdn.magicpatterns.com/uploads/jNq2ZMAW2FWqn9brdgGmCB/Hoja_3.jpg', 'https://cdn.magicpatterns.com/uploads/9gsEZkEXcu9x9AfVyNjQHx/Hoja_4.jpg', 'https://cdn.magicpatterns.com/uploads/w1zWKqtfG3FMVSwQ6CaRyJ/Hoja_5.jpg', 'https://cdn.magicpatterns.com/uploads/cbPaXFEr1oJjZybgknRe7d/Hoja_6.jpg'];
// ── CountdownTimer ──────────────────────────────────────────────────────────
function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState(10 * 60 + 23);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  return <div className="bg-[#dc2626] text-white py-1.5 px-4 sticky top-0 z-50" data-id="element-0">
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-sm font-medium" data-id="element-1">
        <span data-id="element-2">⏳</span>
        <span data-id="element-3">OFERTA EXPIRA EN:</span>
        <span className="bg-white text-[#dc2626] px-2 py-0.5 rounded font-mono font-bold" data-id="element-4">
          {formatTime(timeLeft)}
        </span>
      </div>
    </div>;
}
// ── CTAButton ────────────────────────────────────────────────────────────────
interface CTAButtonProps {
  children: React.ReactNode;
  className?: string;
}
function CTAButton({
  children,
  className = ''
}: CTAButtonProps) {
  return <div className="flex flex-col items-center justify-center w-full" data-id="element-5">
      <a href={SECTION_LINK} className={`
          relative group flex items-center justify-center gap-2
          w-full md:max-w-2xl px-6 py-4 md:py-5
          bg-[#22c55e] hover:bg-[#16a34a]
          text-white text-center
          rounded-lg shadow-xl hover:shadow-2xl
          transform transition-all duration-200 hover:-translate-y-1
          border-b-4 border-[#15803d] active:border-b-0 active:translate-y-1
          text-xl md:text-2xl font-bold uppercase tracking-normal
          ${className}
        `} data-id="element-6">
        {children}
      </a>
    </div>;
}
// ── PageCarousel ─────────────────────────────────────────────────────────────
function PageCarousel() {
  const [current, setCurrent] = useState(0);
  const prev = () => setCurrent(c => c === 0 ? PAGE_IMAGES.length - 1 : c - 1);
  const next = () => setCurrent(c => c === PAGE_IMAGES.length - 1 ? 0 : c + 1);
  return <div className="flex flex-col items-center gap-6" data-id="element-7">
      <style data-id="element-8">{`
        @keyframes fadeSlide {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <div className="relative flex items-center justify-center w-full max-w-sm mx-auto px-12 md:px-0" data-id="element-9">
        {/* Prev button */}
        <button onClick={prev} className="absolute left-0 md:-left-14 z-10 bg-white border border-gray-200 rounded-full w-10 h-10 flex items-center justify-center shadow-md hover:bg-gray-50 active:scale-95 transition-all" aria-label="Anterior" data-id="element-10">
          <ChevronLeftIcon className="w-5 h-5 text-gray-700" data-id="element-11" />
        </button>

        {/* Image */}
        <div className="w-full rounded-2xl overflow-hidden shadow-2xl border border-gray-100" data-id="element-12">
          <img key={current} src={PAGE_IMAGES[current]} alt={`Hoja ${current + 1}`} className="w-full object-cover" style={{
          animation: 'fadeSlide 0.25s ease-out'
        }} data-id="element-13" />
        </div>

        {/* Next button */}
        <button onClick={next} className="absolute right-0 md:-right-14 z-10 bg-white border border-gray-200 rounded-full w-10 h-10 flex items-center justify-center shadow-md hover:bg-gray-50 active:scale-95 transition-all" aria-label="Siguiente" data-id="element-14">
          <ChevronRightIcon className="w-5 h-5 text-gray-700" data-id="element-15" />
        </button>
      </div>

      {/* Dot indicators */}
      <div className="flex gap-2 mt-2" data-id="element-16">
        {PAGE_IMAGES.map((_, i) => <button key={i} onClick={() => setCurrent(i)} className={`rounded-full transition-all duration-200 ${i === current ? 'bg-gray-800 w-6 h-2.5' : 'bg-gray-300 w-2.5 h-2.5'}`} aria-label={`Ir a hoja ${i + 1}`} data-id="element-17" />)}
      </div>

      <p className="text-sm text-gray-400 font-medium" data-id="element-18">
        {current + 1} / {PAGE_IMAGES.length}
      </p>
    </div>;
}
// ── FAQAccordion ─────────────────────────────────────────────────────────────
interface FAQItem {
  question: string;
  answer: string;
}
const faqs: FAQItem[] = [{
  question: '¿Es una dieta estricta?',
  answer: 'No. Es una estructura adaptable con regla de ajuste simple. Podés adaptarla a tu peso, objetivo y contexto sin seguir un plan rígido.'
}, {
  question: '¿Sirve si peso más o menos de 75 kg?',
  answer: 'Sí. Incluye regla ±10% clara para ajustar gramos según tu peso actual. Funciona para cualquier punto de partida.'
}, {
  question: '¿Necesito pesar todo para siempre?',
  answer: 'No. El objetivo es que internalices estructura, no depender eternamente de la balanza. Con el tiempo vas a saber calcular a ojo.'
}, {
  question: '¿Es solo para definición?',
  answer: 'No. Incluye definición, volumen limpio y ajustes avanzados. Tenés tomos específicos para cada objetivo.'
}, {
  question: '¿Cuánto tiempo tengo acceso?',
  answer: 'Acceso de por vida. Sin renovaciones ni pagos adicionales. Una vez que comprás, el contenido es tuyo para siempre.'
}];
function FAQAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <div className="space-y-4 max-w-3xl mx-auto" data-id="element-19">
      {faqs.map((faq, index) => <div key={index} className="border border-gray-200 rounded-lg bg-white overflow-hidden shadow-sm" data-id="element-20">
          <button className="w-full px-6 py-4 text-left flex items-center justify-between bg-white hover:bg-gray-50 transition-colors" onClick={() => setOpenIndex(openIndex === index ? null : index)} data-id="element-21">
            <span className="font-bold text-gray-900 text-lg pr-4" data-id="element-22">
              {faq.question}
            </span>
            {openIndex === index ? <ChevronUpIcon className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-23" /> : <ChevronDownIcon className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-24" />}
          </button>
          {openIndex === index && <div className="px-6 pb-6 pt-2 text-gray-600 leading-relaxed border-t border-gray-100 bg-gray-50/50" data-id="element-25">
              {faq.answer}
            </div>}
        </div>)}
    </div>;
}
// ── TestimonialCard ──────────────────────────────────────────────────────────
interface TestimonialCardProps {
  name: string;
  location: string;
  age: number;
  quote: string;
}
function TestimonialCard({
  name,
  location,
  age,
  quote
}: TestimonialCardProps) {
  return <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 flex flex-col h-full" data-id="element-26">
      <div className="flex gap-1 mb-4" data-id="element-27">
        {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-28" />)}
      </div>
      <p className="text-gray-700 italic mb-6 flex-grow text-lg" data-id="element-29">"{quote}"</p>
      <div className="flex items-center gap-3 mt-auto border-t pt-4 border-gray-100" data-id="element-30">
        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-sm" data-id="element-31">
          {name.charAt(0)}
        </div>
        <div data-id="element-32">
          <p className="font-bold text-gray-900" data-id="element-33">
            {name}, {age}
          </p>
          <p className="text-sm text-gray-500" data-id="element-34">{location}</p>
        </div>
      </div>
    </div>;
}
// ── SocialProofPopup ─────────────────────────────────────────────────────────
const PURCHASES = [{
  name: 'Matías',
  city: 'Buenos Aires'
}, {
  name: 'Nicolás',
  city: 'Córdoba'
}, {
  name: 'Rodrigo',
  city: 'Rosario'
}, {
  name: 'Facundo',
  city: 'Mendoza'
}, {
  name: 'Tomás',
  city: 'La Plata'
}, {
  name: 'Ezequiel',
  city: 'Santa Fe'
}, {
  name: 'Agustín',
  city: 'Tucumán'
}, {
  name: 'Leandro',
  city: 'Neuquén'
}, {
  name: 'Germán',
  city: 'Mar del Plata'
}, {
  name: 'Sebastián',
  city: 'Salta'
}];
function SocialProofPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentPurchase, setCurrentPurchase] = useState(PURCHASES[0]);
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const showPopup = () => {
      const randomPerson = PURCHASES[Math.floor(Math.random() * PURCHASES.length)];
      setCurrentPurchase(randomPerson);
      setIsVisible(true);
      setTimeout(() => {
        setIsVisible(false);
      }, 4000);
      const nextDelay = Math.random() * (15000 - 8000) + 8000 + 4000;
      timeoutId = setTimeout(showPopup, nextDelay);
    };
    const initialTimeout = setTimeout(() => {
      showPopup();
    }, 5000);
    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(timeoutId);
    };
  }, []);
  if (!isVisible) return null;
  return <div className="fixed bottom-4 left-4 z-40" style={{
    animation: 'slideInLeft 0.3s ease-out'
  }} data-id="element-35">
      <style data-id="element-36">{`
        @keyframes slideInLeft {
          from { transform: translateX(-100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
      <div className="bg-white rounded-lg shadow-xl border border-gray-100 p-4 flex items-center gap-3 max-w-xs md:max-w-sm" data-id="element-37">
        <div className="bg-green-100 p-2 rounded-full flex-shrink-0" data-id="element-38">
          <CheckCircleIcon className="w-6 h-6 text-green-600" data-id="element-39" />
        </div>
        <div data-id="element-40">
          <p className="text-sm font-bold text-gray-900" data-id="element-41">
            {currentPurchase.name} de {currentPurchase.city}
          </p>
          <p className="text-xs text-gray-500" data-id="element-42">
            acaba de comprar MegaPack Físico Visible
          </p>
          <p className="text-xs text-green-600 font-medium mt-0.5" data-id="element-43">
            Hace unos instantes
          </p>
        </div>
      </div>
    </div>;
}
// ── App ──────────────────────────────────────────────────────────────────────
export function App() {
  // Meta Pixel
  useEffect(() => {
    if ((window as any).fbq) return;
    const fbq: any = function (...args: any[]) {
      fbq.callMethod ? fbq.callMethod(...args) : fbq.queue.push(args);
    };
    (window as any).fbq = fbq;
    (window as any)._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    fbq('init', '1497007874735639');
    fbq('track', 'PageView');
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = 'https://www.facebook.com/tr?id=1497007874735639&ev=PageView&noscript=1';
    noscript.appendChild(img);
    document.head.appendChild(noscript);
  }, []);
  return <div className="min-h-screen bg-white font-sans text-gray-900 pb-20" data-id="element-44">
      <CountdownTimer data-id="element-45" />
      <SocialProofPopup data-id="element-46" />

      {/* HERO SECTION */}
      <section className="pt-8 pb-12 px-4 md:pt-12 md:pb-20 bg-gradient-to-b from-white to-gray-50" data-id="element-47">
        <div className="max-w-4xl mx-auto text-center" data-id="element-48">
          <div className="inline-flex items-center justify-center bg-green-100 text-green-700 px-6 py-2 rounded-full mb-6" data-id="element-49">
            <span className="text-sm md:text-base font-medium uppercase tracking-wide" data-id="element-50">
              💪 PARA HOMBRES QUE ENTRENAN Y QUIEREN QUE SE NOTE
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black tracking-tight text-gray-900 mb-8 leading-tight" data-id="element-51">
            Lográ definición real en menos de 30 días sin improvisar tus
            comidas.
          </h1>
          <div className="mb-6" data-id="element-52">
            <a href={SECTION_LINK} data-id="element-53">
              <img src={PRODUCT_IMAGE} alt="MegaPack Físico Visible" className="w-full max-w-lg mx-auto drop-shadow-2xl hover:scale-105 transition-transform duration-300 cursor-pointer" data-id="element-54" />
            </a>
          </div>
          <p className="text-xl md:text-2xl font-bold text-gray-900 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-55">
            Organizá tu semana en 30 minutos, reducí delivery y acompañá tu
            entrenamiento con{' '}
            <span className="bg-yellow-300 px-1" data-id="element-56">
              estructura real sin gastar de más.
            </span>
          </p>
          <CTAButton data-id="element-57">👉 SÍ, QUIERO ACCEDER AHORA</CTAButton>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-58">
        <div className="max-w-3xl mx-auto" data-id="element-59">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-60">
            Si entrenás pero comés cualquier cosa, estás desperdiciando
            esfuerzo.
          </h2>
          <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-61">
            <p className="font-medium" data-id="element-62">Entrenás. Transpirás. Metés peso.</p>
            <p className="font-medium" data-id="element-63">Pero el espejo no cambia.</p>
            <p className="font-medium" data-id="element-64">
              No te marcás. No subís masa limpia. No definís.
            </p>
            <p className="font-medium" data-id="element-65">Y el problema no es el gimnasio.</p>
            <p className="font-medium" data-id="element-66">Es que estás improvisando la comida.</p>
            <p className="text-center font-bold text-2xl mt-8 border-l-4 border-[#dc2626] pl-4 py-2 bg-red-50" data-id="element-67">
              Sin estructura en la alimentación, el entrenamiento no alcanza.
            </p>
          </div>
          <div className="mt-12" data-id="element-68">
            <CTAButton data-id="element-69">👉 QUIERO TERMINAR CON ESTA CONFUSIÓN</CTAButton>
          </div>
        </div>
      </section>

      {/* PAIN POINTS SECTION */}
      <section className="py-16 px-4 bg-gray-900 text-white" data-id="element-70">
        <div className="max-w-4xl mx-auto" data-id="element-71">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-yellow-400" data-id="element-72">
            El problema que nadie quiere aceptar
          </h2>
          <div className="flex flex-col gap-6 max-w-3xl mx-auto" data-id="element-73">
            <div className="bg-gray-800 p-8 rounded-xl border border-gray-700" data-id="element-74">
              <ul className="space-y-3" data-id="element-75">
                {['Trabajás o estudiás 8–9 horas.', 'Entrenás después del laburo.', 'Comés lo que hay.', 'Pedís delivery 2 o 3 veces por semana.', 'Comprás proteína… pero sin estructura.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-200" data-id="element-76">
                    <span className="text-gray-500 mt-1" data-id="element-77">•</span>
                    <span className="text-lg font-medium" data-id="element-78">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="bg-gray-800 p-8 rounded-xl border border-gray-700" data-id="element-79">
              <p className="text-2xl font-bold italic text-gray-300 mb-6" data-id="element-80">
                "¿Por qué me veo igual?"
              </p>
              <p className="text-lg font-bold text-white mb-6" data-id="element-81">
                La realidad es simple: Sin sistema de alimentación, no hay
                físico visible.
              </p>
              <div className="space-y-2 font-bold text-red-400 text-xl mb-6" data-id="element-82">
                <p data-id="element-83">❌ No es que no entrenes fuerte.</p>
                <p data-id="element-84">❌ No es que no tengas disciplina.</p>
                <p data-id="element-85">❌ No es que tu genética sea mala.</p>
              </div>
              <p className="text-gray-300 text-lg" data-id="element-86">
                Es que no tenés estructura ejecutable. Y sin estructura, el
                progreso es suerte.
              </p>
            </div>
            <div className="bg-gray-800 p-8 rounded-xl border border-gray-700" data-id="element-87">
              <h3 className="text-xl font-bold text-red-400 mb-4" data-id="element-88">
                Tu problema no es el entrenamiento.
              </h3>
              <p className="text-gray-300 text-lg mb-3" data-id="element-89">
                Es no tener un sistema de alimentación ejecutable.
              </p>
              <p className="text-gray-300 text-lg" data-id="element-90">
                Y nadie te lo entregó así.
              </p>
            </div>
          </div>
          <div className="mt-12 text-center" data-id="element-91">
            <p className="text-2xl font-bold text-[#22c55e]" data-id="element-92">
              👉 MegaPack · Físico Visible nace para eliminar esa improvisación.
            </p>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-93">
        <div className="max-w-5xl mx-auto" data-id="element-94">
          <div className="text-center mb-16" data-id="element-95">
            <span className="bg-green-100 text-green-800 px-4 py-1 rounded-full font-bold text-sm uppercase tracking-wide" data-id="element-96">
              ✅ LA SOLUCIÓN
            </span>
            <h2 className="text-4xl md:text-5xl font-black mt-6 mb-4" data-id="element-97">
              MegaPack · Físico Visible
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-98">
              El sistema completo de alimentación práctica para hombres que
              entrenan en Argentina.
            </p>
            <p className="text-lg text-gray-500 mt-3 max-w-2xl mx-auto" data-id="element-99">
              No es una dieta. No es teoría. No es nutrición clínica.{' '}
              <strong data-id="element-100">Es estructura aplicable.</strong>
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-101">
            <div className="bg-green-50 p-8 rounded-2xl border border-green-100" data-id="element-102">
              <h3 className="text-2xl font-bold mb-6" data-id="element-103">
                Diseñado para que puedas:
              </h3>
              <ul className="space-y-4" data-id="element-104">
                {['Organizar tu alimentación semanal en menos de 30 minutos', 'Reducir delivery innecesario', 'Ahorrar 15–25% en comida mensual', 'Ver cambios físicos visibles en 6–8 semanas', 'Dejar de entrenar "al pedo"'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-105">
                    <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0" data-id="element-106" />
                    <span className="text-lg font-medium text-gray-800" data-id="element-107">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
            <div className="flex flex-col justify-center items-center text-center p-8 bg-gray-50 rounded-2xl border border-gray-100" data-id="element-108">
              <h3 className="text-2xl font-bold mb-6 text-gray-900" data-id="element-109">
                Cómo funciona:
              </h3>
              <div className="space-y-6 text-xl font-bold text-gray-800" data-id="element-110">
                {['Abrís el tomo según tu objetivo', 'Ajustás gramos con la regla simple', 'Ejecutás la semana sin improvisar'].map((step, i) => <div key={i} className="flex items-start gap-4" data-id="element-111">
                    <span className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm flex-shrink-0 mt-1" data-id="element-112">
                      {i + 1}
                    </span>
                    <span className="text-left" data-id="element-113">{step}</span>
                  </div>)}
              </div>
              <p className="mt-8 text-lg text-gray-600 italic" data-id="element-114">
                Sin vueltas. Sin teoría innecesaria. Solo ejecución.
              </p>
            </div>
          </div>
          <CTAButton data-id="element-115">👉 QUIERO EMPEZAR ESTA SEMANA</CTAButton>
        </div>
      </section>

      {/* MODULES SECTION */}
      <section className="py-16 px-4 bg-gray-50" data-id="element-116">
        <div className="max-w-5xl mx-auto" data-id="element-117">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4" data-id="element-118">
            📦 ¿Qué incluye el MegaPack Físico Visible?
          </h2>
          <p className="text-center text-xl text-gray-600 mb-12" data-id="element-119">
            📕 6 TOMOS DEL SISTEMA FÍSICO VISIBLE
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-120">
            {[{
            emoji: '📕',
            title: 'Tomo 1 — Definición Inteligente',
            desc: '4 semanas estructuradas para bajar grasa sin perder músculo, con guía de extensión a 8 semanas.'
          }, {
            emoji: '📗',
            title: 'Tomo 2 — Volumen Limpio',
            desc: 'Sistema de superávit controlado para ganar masa muscular sin acumular grasa innecesaria.'
          }, {
            emoji: '📘',
            title: 'Tomo 3 — Meal Prep de Rendimiento',
            desc: 'Cociná 2 veces por semana y resolvé 7 días completos sin improvisar ni pedir delivery.'
          }, {
            emoji: '📙',
            title: 'Tomo 4 — Desayunos y Snacks de Gimnasio',
            desc: 'Opciones rápidas (≤5 min) con proteína clara para asegurar 60g antes del mediodía.'
          }, {
            emoji: '📓',
            title: 'Tomo 5 — Fitness Económico Argentino',
            desc: 'Estrategias reales para mantener proteína alta sin gastar de más en contexto de inflación.'
          }, {
            emoji: '📒',
            title: 'Tomo 6 — Sistema Completo 30 Días',
            desc: 'Plan estructurado día por día con ajustes, seguimiento y reglas claras para progresar sin fricción.'
          }].map((tomo, i) => <a key={i} href={SECTION_LINK} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer" data-id="element-121">
                <div className="text-4xl mb-4" data-id="element-122">{tomo.emoji}</div>
                <h3 className="text-xl font-bold mb-2" data-id="element-123">{tomo.title}</h3>
                <p className="text-gray-600" data-id="element-124">{tomo.desc}</p>
              </a>)}
          </div>
          <div className="mt-12 bg-white p-6 rounded-xl border border-gray-200 max-w-3xl mx-auto" data-id="element-125">
            <ul className="space-y-3 text-lg" data-id="element-126">
              {['Más de 6 tomos especializados', 'Organizados por objetivo', 'Con regla de ajuste simple', 'Sin contar calorías obsesivamente', 'Con meal prep incluido', 'Pensado para el contexto argentino'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-127">
                  <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-128" />
                  <span className="font-medium" data-id="element-129">{item}</span>
                </li>)}
            </ul>
          </div>
          <p className="text-center text-lg font-medium mt-12 text-gray-700" data-id="element-130">
            👉 Todo estructurado para que sepas qué comer y cuándo hacerlo.
          </p>
        </div>
      </section>

      {/* BEFORE VS AFTER */}
      <section className="py-16 px-4 bg-white" data-id="element-131">
        <div className="max-w-4xl mx-auto" data-id="element-132">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-133">
            Antes vs Después
          </h2>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-134">
            <div className="bg-red-50 p-8 rounded-2xl border border-red-100" data-id="element-135">
              <h3 className="text-xl font-bold text-red-800 mb-6 text-center" data-id="element-136">
                Antes de Físico Visible
              </h3>
              <ul className="space-y-4" data-id="element-137">
                {['Improvisás comidas', 'Delivery frecuente', 'Proteína insuficiente', 'Peso estancado', 'Entrenamiento sin resultados visibles'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-138">
                    <XIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-139" />
                    <span className="text-gray-700" data-id="element-140">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="bg-green-50 p-8 rounded-2xl border border-green-100 shadow-lg transform md:-translate-y-4" data-id="element-141">
              <h3 className="text-xl font-bold text-green-800 mb-6 text-center" data-id="element-142">
                Después de Físico Visible
              </h3>
              <ul className="space-y-4" data-id="element-143">
                {['Semana organizada', 'Compras estratégicas', 'Proteína cubierta', 'Déficit o superávit controlado', 'Cambios visibles en 6–8 semanas'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-144">
                    <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-145" />
                    <span className="font-bold text-gray-800" data-id="element-146">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* BONUSES SECTION */}
      <section className="py-16 px-4 bg-[#0f172a] text-white" data-id="element-147">
        <div className="max-w-5xl mx-auto" data-id="element-148">
          <div className="text-center mb-12" data-id="element-149">
            <span className="bg-yellow-400 text-black px-4 py-1 rounded-full font-bold text-sm uppercase tracking-wide animate-pulse" data-id="element-150">
              🎁 BONOS PREMIUM GRATIS – INCLUIDOS HOY
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mt-6 mb-4" data-id="element-151">
              ¡GRATIS POR HOY!
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-152">
            {[{
            img: BONUS_1_IMAGE,
            alt: 'Bono 1 - Guía de Supermercado Inteligente',
            title: '🎁 Bono 1 – Guía de Supermercado Inteligente',
            desc: 'Cómo comprar proteína suficiente sin regalar plata.'
          }, {
            img: BONUS_2_IMAGE,
            alt: 'Bono 2 - Manual de Salidas y Vida Social',
            title: '🎁 Bono 2 – Manual de Salidas y Vida Social',
            desc: 'Qué hacer en asados y fines de semana sin sabotear resultados.'
          }, {
            img: BONUS_3_IMAGE,
            alt: 'Bono 3 - Sistema de Progreso Visual y Medición Profesional',
            title: '🎁 Bono 3 – Sistema de Progreso Visual y Medición Profesional',
            desc: 'Cómo medir cambios reales sin depender del espejo.'
          }].map((bonus, i) => <a key={i} href={SECTION_LINK} className="bg-[#1e293b] rounded-xl p-6 border border-[#334155] flex flex-col items-center text-center hover:border-yellow-400 transition-colors cursor-pointer" data-id="element-153">
                <div className="w-full aspect-square max-w-[270px] mb-6 flex items-center justify-center rounded-lg bg-[#1e293b]" data-id="element-154">
                  <img src={bonus.img} alt={bonus.alt} className="w-full h-full object-contain drop-shadow-xl" data-id="element-155" />
                </div>
                <span className="bg-yellow-400 text-black px-3 py-1 rounded-full text-xs font-bold mb-3" data-id="element-156">
                  GRATIS HOY
                </span>
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-157">
                  {bonus.title}
                </h3>
                <p className="text-gray-300 text-sm" data-id="element-158">{bonus.desc}</p>
              </a>)}
          </div>
          <div className="mt-12 text-center space-y-2 text-lg font-medium" data-id="element-159">
            <p data-id="element-160">👉 Los tomos te dan estructura.</p>
            <p data-id="element-161">👉 Los bonos evitan que pierdas progreso.</p>
            <p className="text-yellow-400 font-bold text-xl mt-4" data-id="element-162">
              Eso es lo que hace que funcione.
            </p>
          </div>
          <div className="mt-12" data-id="element-163">
            <CTAButton data-id="element-164">QUIERO MIS BONOS AHORA</CTAButton>
          </div>
        </div>
      </section>

      {/* OFFER STACK SECTION */}
      <section id="compra" className="py-16 px-4 bg-gradient-to-b from-gray-900 to-gray-800" data-id="element-165">
        <div className="max-w-4xl mx-auto" data-id="element-166">
          <div className="text-center mb-10" data-id="element-167">
            <span className="bg-red-500 text-white px-4 py-2 rounded-full font-bold text-sm uppercase tracking-wide animate-pulse inline-block mb-4" data-id="element-168">
              ⏰ OFERTA ESPECIAL – Tiempo Limitado
            </span>
            <h2 className="text-4xl md:text-6xl font-black text-white uppercase leading-tight" data-id="element-169">
              POR TIEMPO LIMITADO
            </h2>
          </div>
          <div className="bg-white rounded-3xl p-6 md:p-12 shadow-2xl" data-id="element-170">
            <h3 className="text-2xl md:text-3xl font-black text-center mb-8 text-gray-900" data-id="element-171">
              📦 Lo que recibís hoy:
            </h3>
            <div className="space-y-4 mb-10" data-id="element-172">
              {['6 Tomos completos del sistema Físico Visible', '3 Bonos Premium', 'Acceso digital inmediato', 'Acceso de por vida'].map((item, i) => <div key={i} className={`flex items-center gap-3 text-lg md:text-xl ${i < 3 ? 'border-b border-gray-200 pb-3' : ''}`} data-id="element-173">
                  <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0" data-id="element-174" />
                  <span className="font-bold text-gray-800" data-id="element-175">{item}</span>
                </div>)}
            </div>
            <div className="text-center mb-8" data-id="element-176">
              <p className="text-sm text-gray-500 font-medium mb-1" data-id="element-177">
                Precio regular: <span className="line-through" data-id="element-178">49.990 ARS</span>
              </p>
              <div className="flex items-center justify-center gap-3" data-id="element-179">
                <span className="text-5xl" data-id="element-180">🔥</span>
                <span className="text-5xl md:text-6xl font-black text-[#22c55e] leading-none" data-id="element-181">
                  19.990 ARS
                </span>
              </div>
              <p className="text-gray-500 font-medium mt-2 text-sm" data-id="element-182">
                Un solo pago. Sin suscripciones.
              </p>
            </div>
            <div className="flex flex-col items-center justify-center w-full" data-id="element-183">
              <a href={CHECKOUT_LINK} className="relative group flex items-center justify-center gap-2 w-full md:max-w-2xl px-6 py-5 md:py-6 bg-[#22c55e] hover:bg-[#16a34a] text-white text-center rounded-xl shadow-xl hover:shadow-2xl transform transition-all duration-200 hover:-translate-y-1 border-b-4 border-[#15803d] active:border-b-0 active:translate-y-1 text-xl md:text-2xl font-bold uppercase tracking-normal" data-id="element-184">
                👉 OBTENER MI MEGAPACK AHORA
              </a>
            </div>
          </div>
          <div className="mt-10 flex flex-col md:flex-row items-center gap-6 bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20" data-id="element-185">
            <ShieldCheckIcon className="w-16 h-16 text-yellow-400 flex-shrink-0" data-id="element-186" />
            <div className="text-center md:text-left" data-id="element-187">
              <h3 className="text-xl font-bold mb-2 text-white" data-id="element-188">
                Garantía de 30 días sin riesgo
              </h3>
              <p className="text-gray-300" data-id="element-189">
                Probalo durante 30 días. Si no sentís que el sistema te dio
                estructura real, podés pedir reembolso. Sin vueltas. Sin
                preguntas innecesarias.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PREVIEW PAGES SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-190">
        <div className="max-w-2xl mx-auto" data-id="element-191">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4" data-id="element-192">
            📖 Revisá algunas hojas
          </h2>
          <p className="text-center text-gray-600 mb-10" data-id="element-193">
            Mirá una muestra real del contenido.
          </p>
          <PageCarousel data-id="element-194" />
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 px-4 bg-gray-50" data-id="element-195">
        <div className="max-w-6xl mx-auto" data-id="element-196">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-197">
            Lo que dicen los que ya lo tienen
          </h2>
          <div className="grid md:grid-cols-3 gap-6" data-id="element-198">
            <TestimonialCard name="Matías" age={24} location="Buenos Aires" quote="Llevaba 2 años entrenando y el espejo no cambiaba. Con el sistema de Físico Visible organicé mi alimentación en una semana. En 6 semanas ya se notaba la diferencia." data-id="element-199" />
            <TestimonialCard name="Nicolás" age={28} location="Córdoba" quote="Pedía delivery 3 veces por semana y compraba proteína sin estructura. El Tomo 3 de Meal Prep me cambió todo. Cocino 2 veces y resuelvo la semana." data-id="element-200" />
            <TestimonialCard name="Rodrigo" age={31} location="Rosario" quote="Siempre creí que necesitaba un nutricionista caro. Esto es más práctico y ejecutable. En 8 semanas bajé 5 kilos de grasa sin perder fuerza." data-id="element-201" />
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-6" data-id="element-202">
            <TestimonialCard name="Facundo" age={26} location="Mendoza" quote="El tomo de Fitness Económico vale oro. Con la inflación que hay, aprendí a mantener proteína alta sin gastar el doble. Ahorro 20% por mes." data-id="element-203" />
            <TestimonialCard name="Tomás" age={22} location="La Plata" quote="Soy estudiante, no tengo tiempo. Los desayunos y snacks de 5 minutos me aseguran proteína antes del mediodía. Simple y efectivo." data-id="element-204" />
            <TestimonialCard name="Ezequiel" age={33} location="Santa Fe" quote="Entrenaba hace 4 años y nunca definí de verdad. Con el Tomo 1 de Definición Inteligente, en 30 días ya vi cambios que nunca había logrado antes." data-id="element-205" />
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-206">
        <div className="max-w-3xl mx-auto" data-id="element-207">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-208">
            Preguntas frecuentes
          </h2>
          <FAQAccordion data-id="element-209" />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 px-4 bg-gray-900 text-white text-center" data-id="element-210">
        <div className="max-w-3xl mx-auto" data-id="element-211">
          <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-212">
            Si entrenás, que se vea.
          </h2>
          <p className="text-xl text-gray-300 mb-6 leading-relaxed" data-id="element-213">
            No necesitás más motivación. No necesitás otro pre entreno.
          </p>
          <p className="text-xl text-gray-300 mb-6 leading-relaxed" data-id="element-214">
            Necesitás sistema.
          </p>
          <p className="text-2xl text-white font-bold mb-10" data-id="element-215">
            Organizá tu alimentación esta semana. Empezá a progresar.
          </p>
          <a href={SECTION_LINK} className="inline-flex flex-col items-center justify-center px-12 py-5 md:py-6 bg-[#22c55e] hover:bg-[#16a34a] text-white text-center rounded-xl shadow-xl hover:shadow-2xl transform transition-all duration-200 hover:-translate-y-1 border-b-4 border-[#15803d] active:border-b-0 active:translate-y-1" data-id="element-216">
            <span className="text-lg md:text-xl font-bold uppercase tracking-normal" data-id="element-217">
              👉 ACCEDÉ AHORA AL MEGAPACK FÍSICO VISIBLE
            </span>
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 bg-black text-gray-500 text-center text-sm" data-id="element-218">
        <p data-id="element-219">© 2024 MegaPack Físico Visible. Todos los derechos reservados.</p>
        <p className="mt-2" data-id="element-220">
          Este sitio no es parte del sitio web de Facebook o Facebook Inc.
        </p>
      </footer>
    </div>;
}